/**
 * UserProfile - Página de perfil del usuario con opción para cambiar contraseña
 */

import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useToast } from '../components/Toast/useToast';

export default function UserProfile() {
  const { user, userData, company, role } = useAuth();
  const { showToast } = useToast();
  
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      showToast({ type: 'error', message: 'Las contraseñas no coinciden' });
      return;
    }

    if (newPassword.length < 6) {
      showToast({ type: 'error', message: 'La contraseña debe tener al menos 6 caracteres' });
      return;
    }

    try {
      setLoading(true);
      
      // Reautenticar al usuario con su contraseña actual
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Cambiar la contraseña
      await updatePassword(user, newPassword);
      
      showToast({ type: 'success', message: '✅ Contraseña actualizada correctamente' });
      
      // Limpiar formulario
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowChangePassword(false);
    } catch (error) {
      console.error('Error cambiando contraseña:', error);
      
      if (error.code === 'auth/wrong-password') {
        showToast({ type: 'error', message: 'La contraseña actual es incorrecta' });
      } else if (error.code === 'auth/too-many-requests') {
        showToast({ type: 'error', message: 'Demasiados intentos. Espera unos minutos' });
      } else {
        showToast({ type: 'error', message: 'Error al cambiar la contraseña' });
      }
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Administrador';
      case 'company_admin': return 'Administrador';
      case 'company_user': return 'Usuario';
      default: return 'Usuario';
    }
  };

  return (
    <div className="container">
      <div className="header">
        <span className="badge">FIRMAS CEJM</span>
      </div>

      <h1 className="title">👤 Mi Perfil</h1>

      <div className="card">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          
          {/* Información del usuario */}
          <div>
            <h3 style={{ marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Información Personal</h3>
            
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <div className="meta" style={{ marginBottom: 4 }}>Nombre</div>
                <div style={{ fontSize: 15, fontWeight: 500 }}>
                  {userData?.displayName || userData?.name || 'No especificado'}
                </div>
              </div>

              <div>
                <div className="meta" style={{ marginBottom: 4 }}>Email</div>
                <div style={{ fontSize: 15 }}>{user?.email}</div>
              </div>

              <div>
                <div className="meta" style={{ marginBottom: 4 }}>Rol</div>
                <div style={{ fontSize: 15 }}>
                  <span className="badge" style={{ 
                    background: role === 'super_admin' ? '#dbeafe' : role === 'company_admin' ? '#dcfce7' : '#fef3c7',
                    color: role === 'super_admin' ? '#1e40af' : role === 'company_admin' ? '#166534' : '#92400e',
                    border: `1px solid ${role === 'super_admin' ? '#bfdbfe' : role === 'company_admin' ? '#bbf7d0' : '#fde68a'}`,
                    padding: '6px 12px'
                  }}>
                    {getRoleDisplay(role)}
                  </span>
                </div>
              </div>

              {company && (
                <div>
                  <div className="meta" style={{ marginBottom: 4 }}>Empresa</div>
                  <div style={{ fontSize: 15, fontWeight: 500 }}>{company.name}</div>
                </div>
              )}

              <div>
                <div className="meta" style={{ marginBottom: 4 }}>ID de Usuario</div>
                <div style={{ fontSize: 13, fontFamily: 'monospace', color: '#64748b' }}>{user?.uid}</div>
              </div>
            </div>
          </div>

          {/* Separador */}
          <div style={{ borderTop: '1px solid #e2e8f0' }} />

          {/* Seguridad */}
          <div>
            <h3 style={{ marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Seguridad</h3>
            
            {!showChangePassword ? (
              <button 
                className="btn" 
                onClick={() => setShowChangePassword(true)}
                style={{ width: 'auto' }}
              >
                🔒 Cambiar Contraseña
              </button>
            ) : (
              <form onSubmit={handleChangePassword} style={{ maxWidth: 500 }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                    Contraseña Actual
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    style={{ width: '100%' }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                    Nueva Contraseña
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    style={{ width: '100%' }}
                  />
                  <p className="meta" style={{ marginTop: 4, fontSize: 12 }}>
                    Mínimo 6 caracteres
                  </p>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                    Confirmar Nueva Contraseña
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    style={{ width: '100%' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <button 
                    type="submit" 
                    className="btn"
                    disabled={loading}
                    style={{ opacity: loading ? 0.6 : 1 }}
                  >
                    {loading ? '⏳ Cambiando...' : '✅ Cambiar Contraseña'}
                  </button>
                  
                  <button 
                    type="button"
                    className="copy"
                    onClick={() => {
                      setShowChangePassword(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            )}
          </div>

        </div>
      </div>

      {/* Información adicional */}
      <div style={{ marginTop: 20, padding: 16, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <p className="meta" style={{ margin: 0, fontSize: 13 }}>
          💡 <strong>Tip:</strong> Si tienes problemas para iniciar sesión, contacta con tu administrador.
        </p>
      </div>
    </div>
  );
}
